/**
 * openExternal — open a URL outside the React-router context.
 *
 * Behaviour:
 *   - **Native + `mode: 'system'`**: opens in the DEFAULT BROWSER app
 *     (real Safari / Chrome), leaving TruckTrackr entirely. REQUIRED for
 *     payment flows: App Review (Aug 2026, Guideline 3.1.1) rejected
 *     SFSafariViewController checkout as an in-app purchase mechanism —
 *     US storefront apps must "link out to the default browser" instead.
 *
 *   - **Native (other modes)**: opens in SFSafariViewController (iOS) /
 *     Chrome Custom Tabs (Android). User stays inside the app shell —
 *     fine for docs, profiles, and other non-payment links.
 *
 *   - **Web** with `mode: 'same-tab'` (DEFAULT): plain
 *     `window.location.href = url`. This is what Stripe Checkout / OAuth
 *     redirect flows need — they round-trip back to the same tab with a
 *     `?session_id=` or `?code=` query string we can read.
 *
 *   - **Web** with `mode: 'new-tab'`: `window.open(url, '_blank',
 *     'noopener,noreferrer')` — for genuinely external links (privacy
 *     policy on a third-party site, "share" links to other tools).
 */
import { Browser } from '@capacitor/browser';
import { AppLauncher } from '@capacitor/app-launcher';
import { Capacitor } from '@capacitor/core';

export type OpenExternalMode = 'same-tab' | 'new-tab' | 'system';

export async function openExternal(
  url: string,
  opts: { mode?: OpenExternalMode } = {},
): Promise<void> {
  if (!url) return;
  const mode = opts.mode ?? 'same-tab';

  if (Capacitor.isNativePlatform()) {
    if (mode === 'system') {
      // Hand off to the OS default browser (Guideline 3.1.1 link-out).
      await AppLauncher.openUrl({ url });
      return;
    }
    // In-app browser — closes back to TruckTrackr automatically when the
    // user dismisses. We tint the system chrome to brand orange so it
    // doesn't feel like the user has been kicked out of the app.
    await Browser.open({
      url,
      toolbarColor: '#EA580C',
      presentationStyle: 'fullscreen',
    });
    return;
  }

  if (mode === 'new-tab') {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    window.location.href = url;
  }
}
